import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import MapControls from "./MapControls";
import MapLegend from "./MapLegend";
import MapToolsMenu from "./MapToolsMenu";
import WeatherOverlay from "./WeatherOverlay";
import {
  OPEN_MAP_FILTERS_EVENT,
  TOGGLE_MAP_LAYERS_EVENT,
  TOGGLE_MAP_MEASURE_EVENT,
} from "./mapToolsEvents";

function WeatherOverlayInstance({ map }) {
  return (
    <WeatherOverlay
      data={map.data}
      isOpen={map.overlayOpen}
      initialTab={map.overlayTab}
      onTabChange={map.setOverlayTab}
      panelView={map.overlayPanelView}
      onPanelViewChange={map.setOverlayPanelView}
      fish={map.overlayFish}
      onPickFish={(fishName) => map.setOverlayFish({ name: fishName, source: "map" })}
      onCloseFish={() => map.setOverlayFish(null)}
      onFishipediaNavigate={map.handleMapSpeciesFishipediaNavigate}
      loading={map.weatherLoading}
      error={map.weatherError}
      onOpenProfile={map.onOpenProfile}
      onOpenGroup={map.onOpenGroup}
      viewer={map.viewer}
      onAddFavoriteSpot={map.onAddFavoriteSpot}
      isSimpleModeActive={map.isSimpleModeActive}
      onClose={map.handleOverlayClose}
    />
  );
}

function DesktopRightPanelWeatherOverlay({ map }) {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (!map.desktopRightPanelTargetId || typeof document === "undefined") {
      setTarget(null);
      return undefined;
    }

    const resolveTarget = () => {
      setTarget(document.getElementById(map.desktopRightPanelTargetId));
    };
    const timeoutId = window.setTimeout(resolveTarget, 0);
    resolveTarget();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [map.desktopRightPanelTargetId]);

  if (!target) {
    return null;
  }

  return createPortal(<WeatherOverlayInstance map={map} />, target);
}

function MapLocationPermissionDialog({ dialog, onRetry, onClose }) {
  const { t } = useTranslation();

  if (!dialog) {
    return null;
  }

  return (
    <div
      className="map-location-permission-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="map-location-permission-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-location-permission-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h3
          id="map-location-permission-title"
          className="map-location-permission-dialog__title"
        >
          {t(dialog.titleKey || "map_location_permission_title")}
        </h3>
        <p className="map-location-permission-dialog__message">
          {t(dialog.messageKey)}
        </p>
        <div className="map-location-permission-dialog__actions">
          <button
            type="button"
            className="map-location-permission-dialog__button map-location-permission-dialog__button--secondary"
            onClick={onClose}
          >
            {t("close")}
          </button>
          <button
            type="button"
            className="map-location-permission-dialog__button"
            onClick={onRetry}
          >
            {t("try_again")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MapOverlayCoordinator({ map }) {
  const dispatchMapToolEvent = (eventName) => {
    window.dispatchEvent(new Event(eventName));
  };

  return (
    <>
      <div className="map-controls-overlay">
        <MapControls
          activePage={map.activePage}
          selectedMarkerFilters={map.selectedMarkerFilters}
          onMarkerFilterChange={map.setSelectedMarkerFilters}
          availableSpeciesOptions={map.availableSpeciesOptions}
          selectedSpeciesFilters={map.selectedSpeciesFilters}
          onSpeciesFilterChange={map.setSelectedSpeciesFilters}
          availableTypeOfFishingOptions={map.availableTypeOfFishingOptions}
          selectedTypeOfFishingFilter={map.selectedTypeOfFishingFilter}
          onTypeOfFishingFilterChange={map.setSelectedTypeOfFishingFilter}
          availableWayOfFishingOptions={map.availableWayOfFishingOptions}
          selectedWayOfFishingFilter={map.selectedWayOfFishingFilter}
          onWayOfFishingFilterChange={map.setSelectedWayOfFishingFilter}
        />
      </div>

      {map.activePage === "map" && (
        <MapToolsMenu
          onOpenFilters={() => dispatchMapToolEvent(OPEN_MAP_FILTERS_EVENT)}
          onOpenLayers={() => dispatchMapToolEvent(TOGGLE_MAP_LAYERS_EVENT)}
          onToggleMeasure={() => dispatchMapToolEvent(TOGGLE_MAP_MEASURE_EVENT)}
          onLocateMe={map.handleLocateMe}
          isLocationPanelOpen={map.overlayOpen}
          isCatchMarkerDetailsOpen={Boolean(map.selectedSpot)}
        />
      )}

      <MapLocationPermissionDialog
        dialog={map.locationPermissionDialog}
        onRetry={map.handleRetryLocationPermission}
        onClose={map.handleCloseLocationPermissionDialog}
      />

      <MapLegend
        selectedSpot={map.selectedSpot}
        posts={map.posts}
        onClose={map.handleCloseSelectedSpot}
        onOpenProfile={map.onOpenProfile}
        onAddFavoriteSpot={map.onAddFavoriteSpot}
        viewer={map.viewer}
        onUserUpdated={map.onUserUpdated}
        showReviews={map.spotReviewsOpen}
        onShowReviewsChange={map.setSpotReviewsOpen}
        selectedPostInfo={map.spotSelectedPostInfo}
        onSelectedPostInfoChange={map.setSpotSelectedPostInfo}
        showDanger={map.spotDangerOpen}
        onShowDangerChange={map.setSpotDangerOpen}
        expandedImage={map.spotExpandedImage}
        onExpandedImageChange={map.setSpotExpandedImage}
        expandedSource={map.spotExpandedImageSource}
        onExpandedSourceChange={map.setSpotExpandedImageSource}
        showShareModal={map.spotShareOpen}
        onShowShareModalChange={map.setSpotShareOpen}
        showLocationConsent={map.spotLocationConsentOpen}
        onShowLocationConsentChange={map.setSpotLocationConsentOpen}
        onPostInfoNavigateAway={map.handleSpotPostInfoNavigateAway}
      />

      {map.useDesktopRightPanel ? (
        <DesktopRightPanelWeatherOverlay map={map} />
      ) : (
        <WeatherOverlayInstance map={map} />
      )}
    </>
  );
}
